import { useEffect, useState } from "react";
import { api } from "../api/client";
import {
  Box,
  TextField,
  Button,
  Stack,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BlockIcon from '@mui/icons-material/Block';

export default function DeviceTokens() {
  const [description, setDescription] = useState("");
  const [tokens, setTokens] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [newToken, setNewToken] = useState(null);
  const [showTokenDialog, setShowTokenDialog] = useState(false);

  async function loadTokens() {
    try {
      const list = await api("/admin/device-tokens");
      setTokens(list);
    } catch (err) {
      console.error("Failed to load tokens:", err);
    }
  }

  useEffect(() => {
    loadTokens();
  }, []);

  async function generateToken() {
    setError(null);
    setSuccess(null);
    try {
      const result = await api("/admin/device-tokens", {
        method: "POST",
        body: JSON.stringify({ description })
      });
      setNewToken(result.token);
      setShowTokenDialog(true);
      setDescription("");
      await loadTokens();
    } catch (err) {
      setError(err.message || "Failed to generate token");
    }
  }

  async function deleteToken(id) {
    if (!window.confirm("Are you sure you want to delete this token? Devices using this token will lose access.")) {
      return;
    }
    try {
      await api(`/admin/device-tokens/${id}`, {
        method: "DELETE"
      });
      setSuccess("Token deleted successfully");
      await loadTokens();
    } catch (err) {
      setError(err.message || "Failed to delete token");
    }
  }

  async function revokeToken(id) {
    if (!window.confirm("Are you sure you want to revoke this token? It cannot be reactivated.")) {
      return;
    }
    try {
      await api(`/admin/device-tokens/${id}/revoke`, {
        method: "PATCH"
      });
      setSuccess("Token revoked successfully");
      await loadTokens();
    } catch (err) {
      setError(err.message || "Failed to revoke token");
    }
  }

  function copyToClipboard(text) {
    navigator.clipboard.writeText(text).then(() => {
      setSuccess("Token copied to clipboard!");
      setTimeout(() => setSuccess(null), 3000);
    });
  }

  function handleCloseDialog() {
    setShowTokenDialog(false);
    setNewToken(null);
  }

  return (
    <Box sx={{ backgroundColor: '#000', minHeight: '100vh', p: 3 }}>
      <Typography variant="h6" mb={1} sx={{ color: '#fff' }}>Device Tokens</Typography>
      <Typography variant="body2" mb={2} sx={{ color: '#999' }}>
        Generate secure tokens for Raspberry Pi devices to authenticate with the control plane.
      </Typography>

      <Stack spacing={2} direction="row" mb={3} alignItems="center">
        <TextField
          label="Description"
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="e.g., Terminal 1 Devices"
          sx={{
            width: 400,
            '& .MuiOutlinedInput-root': { color: '#fff', '& fieldset': { borderColor: '#666' }, '&:hover fieldset': { borderColor: '#999' } },
            '& .MuiInputLabel-root': { color: '#999' },
            '& .MuiInputLabel-root.Mui-focused': { color: '#fff' }
          }}
        />
        <Button
          variant="contained"
          onClick={generateToken}
          disabled={!description}
        >
          Generate Token
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <TableContainer component={Paper} sx={{ bgcolor: '#000', boxShadow: 'none' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#2a2a2a' }}>
              <TableCell sx={{ fontWeight: 600, color: '#fff', borderBottom: '2px solid #333' }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#fff', borderBottom: '2px solid #333' }}>Token (Prefix)</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#fff', borderBottom: '2px solid #333' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#fff', borderBottom: '2px solid #333' }}>Created</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#fff', borderBottom: '2px solid #333' }}>Last Used</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, color: '#fff', borderBottom: '2px solid #333' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tokens.length === 0 && (
              <TableRow sx={{ bgcolor: '#1a1a1a' }}>
                <TableCell colSpan={6} align="center" sx={{ py: 3, color: '#999' }}>
                  No tokens created yet. Generate your first token above.
                </TableCell>
              </TableRow>
            )}
            {tokens.map((t, index) => {
              const isEvenRow = index % 2 === 0;
              return (
                <TableRow key={t._id} sx={{ bgcolor: isEvenRow ? '#1a1a1a' : '#000', '&:hover': { bgcolor: '#2a2a2a' } }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>{t.description}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    <code style={{ fontSize: '0.85em', color: '#fff', fontWeight: 'bold' }}>
                      {t.token
                        ? `${t.token.substring(0, 20)}...`
                        : t.tokenHash
                          ? `${t.tokenHash.substring(0, 12)}... (hashed)`
                          : 'N/A'}
                    </code>
                    {t.token && (
                      <IconButton
                        size="small"
                        onClick={() => copyToClipboard(t.token)}
                        title="Copy full token"
                        sx={{ color: '#999' }}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    {t.active ? (
                      <Chip label="Active" color="success" size="small" />
                    ) : (
                      <Chip label="Revoked" color="error" size="small" />
                    )}
                  </TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                    {new Date(t.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                    {t.lastUsed ? new Date(t.lastUsed).toLocaleString() : 'Never'}
                  </TableCell>
                  <TableCell align="right">
                    {t.active && (
                      <IconButton
                        size="small"
                        color="warning"
                        onClick={() => revokeToken(t._id)}
                        title="Revoke token"
                      >
                        <BlockIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => deleteToken(t._id)}
                      title="Delete token"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* New Token Dialog */}
      <Dialog open={showTokenDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>Token Generated Successfully</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            <strong>Important:</strong> Copy this token now. You won't be able to see it again!
          </DialogContentText>
          <Paper sx={{ p: 2, bgcolor: 'grey.100', wordBreak: 'break-all', fontFamily: 'monospace' }}>
            {newToken}
          </Paper>
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              startIcon={<ContentCopyIcon />}
              onClick={() => copyToClipboard(newToken)}
            >
              Copy Token
            </Button>
          </Box>
          <Alert severity="info" sx={{ mt: 2 }}>
            Use this token in your device configuration:
            <pre style={{ marginTop: 8 }}>
              DEVICE_TOKEN={newToken}
            </pre>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
